/**
 * FlexAssetConsumptionChart - Per-asset hourly consumption chart with optional temperature overlay.
 *
 * Props:
 *   - flexAssets: Array of flexible assets to choose from
 *   - selectedAssetId: Currently selected asset ID
 *   - onAssetChange: Callback when asset selection changes
 *   - assetSchedule: { power_kw: number[24], temperature_c?: number[24] }
 *   - showTemperature: Boolean to show temperature overlay (for heat pump/HVAC)
 *
 * Displays:
 *   - Dropdown to select which flex asset to view
 *   - Primary chart: Hourly kW consumption (bar chart)
 *   - Secondary Y-axis (optional): Temperature °C overlay (line) for thermal assets
 */

import React, { useMemo, useState } from 'react'
import Chart from 'react-apexcharts'
import { Form } from 'react-bootstrap'

// Generate mock asset consumption data
const generateMockAssetData = (assetType) => {
  const hours = Array.from({ length: 24 }, (_, i) => i)

  if (assetType === 'heat_pump') {
    // Heat pump: 2-4 kW when ON, temperature pattern
    const power = hours.map((h) => {
      if (h >= 0 && h <= 5) return 3.5 + Math.random() * 0.5 // Night heating
      if (h >= 14 && h <= 16) return 3.0 + Math.random() * 0.5 // Pre-heating
      return 0
    })
    const temperature = hours.map((h) => {
      // Temperature drops overnight, rises during day, maintained by heating
      if (h >= 0 && h <= 6) return 18 + h * 0.5 // Heating up from 18 to 21
      if (h >= 7 && h <= 13) return 21 + (h - 7) * 0.14 // Drifts up to 22
      if (h >= 14 && h <= 16) return 22 - (h - 14) * 0.3 // Pre-heating back to 21
      if (h >= 17 && h <= 23) return 21 - (h - 17) * 0.3 // Drifts down to 19
      return 21
    })
    return { power, temperature }
  } else if (assetType === 'hvac') {
    // HVAC: 3-5 kW when ON, cooling pattern
    const power = hours.map((h) => {
      if (h >= 8 && h <= 11) return 4.0 + Math.random() * 0.8 // Morning cooling
      if (h >= 14 && h <= 17) return 4.5 + Math.random() * 0.8 // Afternoon cooling
      return 0
    })
    const temperature = hours.map((h) => {
      // Temperature rises during day, reduced by cooling
      if (h >= 0 && h <= 7) return 18 + h * 0.3 // Warms up to 20
      if (h >= 8 && h <= 11) return 22 - (h - 8) * 0.5 // Cooling to 20
      if (h >= 12 && h <= 13) return 20 + (h - 12) * 1.5 // Warms to 23
      if (h >= 14 && h <= 17) return 24 - (h - 14) * 0.75 // Cooling to 21
      if (h >= 18 && h <= 23) return 21 - (h - 18) * 0.3 // Cools to 19
      return 22
    })
    return { power, temperature }
  } else if (assetType === 'ev_charger') {
    // EV charger: constant 7 kW when ON
    const power = hours.map((h) => {
      if ((h >= 0 && h <= 5) || h === 23) return 7.0
      return 0
    })
    return { power, temperature: null }
  } else {
    // Generic flexible appliance
    const power = hours.map((h) => {
      if (h >= 13 && h <= 14) return 2.5 + Math.random() * 0.5
      return 0
    })
    return { power, temperature: null }
  }
}

function FlexAssetConsumptionChart({ flexAssets, selectedAssetId, onAssetChange, assetSchedule, showTemperature = true }) {
  // Mock flex assets if not provided
  const mockAssets = [
    { id: 'hp_b1', name: 'Heat Pump - Building A', type: 'heat_pump' },
    { id: 'hvac_b2', name: 'HVAC - Building B', type: 'hvac' },
    { id: 'ev_b1', name: 'EV Charger - Building A', type: 'ev_charger' },
    { id: 'wm_b3', name: 'Washing Machine - Building C', type: 'flexible_appliance' },
  ]

  // Use real data when provided, show empty state if no flex assets
  if (!flexAssets && !assetSchedule) {
    return (
      <div>
        <h6 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, marginBottom: 'var(--space-3)', color: 'var(--color-neutral-700)' }}>
          Per-Asset Hourly Consumption
        </h6>
        <div className="text-center py-4" style={{ color: 'var(--color-neutral-500)' }}>
          <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, margin: 0 }}>No flexible load data available</p>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-400)', marginTop: 'var(--space-2)' }}>
            Configure flexible assets (heat pumps, EV chargers) on buildings to see per-asset consumption.
          </p>
        </div>
      </div>
    )
  }
  const assets = flexAssets || mockAssets
  const [selectedId, setSelectedId] = useState(selectedAssetId || assets[0]?.id)
  const selectedAsset = assets.find((a) => a.id === selectedId) || assets[0]

  const handleAssetChange = (newId) => {
    setSelectedId(newId)
    if (onAssetChange) onAssetChange(newId)
  }

  // Use provided schedule, or build from selected asset's embedded data, or generate mock
  const scheduleData = useMemo(() => {
    if (assetSchedule) return assetSchedule
    // If assets carry their own power/temperature arrays (from optimizer), use those
    if (selectedAsset?.power) {
      return { power: selectedAsset.power, temperature: selectedAsset.temperature || null }
    }
    return generateMockAssetData(selectedAsset?.type || 'heat_pump')
  }, [assetSchedule, selectedAsset])

  const hours = Array.from({ length: 24 }, (_, i) => i)

  // Determine if we should show temperature (only for heat_pump and hvac)
  const isThermalAsset = selectedAsset?.type === 'heat_pump' || selectedAsset?.type === 'hvac'
  const shouldShowTemp = showTemperature && isThermalAsset && scheduleData.temperature

  // ApexCharts configuration
  const chartOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: { show: true },
      animations: { enabled: true, speed: 800 },
    },
    colors: ['#8CB808', '#E74C3C'],
    plotOptions: {
      bar: {
        borderRadius: 4,
        columnWidth: '80%',
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      width: [0, 3],
      curve: 'smooth',
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      markers: { width: 10, height: 10, radius: 2 },
    },
    xaxis: {
      categories: hours,
      title: { text: 'Hour of Day', style: { fontSize: '12px', fontWeight: 500 } },
      labels: { style: { fontSize: '11px' } },
    },
    yaxis: [
      {
        title: { text: 'Power (kW)', style: { fontSize: '12px', fontWeight: 500 } },
        labels: {
          style: { fontSize: '11px' },
          formatter: (value) => value != null ? value.toFixed(1) : '',
        },
      },
      ...(shouldShowTemp
        ? [
            {
              opposite: true,
              title: { text: 'Temperature (°C)', style: { fontSize: '12px', fontWeight: 500 } },
              labels: {
                style: { fontSize: '11px' },
                formatter: (value) => value != null ? value.toFixed(1) : '',
              },
            },
          ]
        : []),
    ],
    tooltip: {
      shared: true,
      intersect: false,
      y: [
        { formatter: (value) => value != null ? `${value.toFixed(2)} kW` : '' },
        ...(shouldShowTemp ? [{ formatter: (value) => value != null ? `${value.toFixed(1)} °C` : '' }] : []),
      ],
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 3,
    },
  }

  const series = [
    {
      name: 'Power Consumption',
      type: 'bar',
      data: scheduleData.power,
    },
    ...(shouldShowTemp
      ? [
          {
            name: 'Indoor Temperature',
            type: 'line',
            data: scheduleData.temperature,
          },
        ]
      : []),
  ]

  return (
    <div className="flex-asset-consumption-chart-container">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 style={{ fontSize: 'var(--font-size-base)', fontWeight: 600, margin: 0, color: 'var(--color-neutral-700)' }}>
          Per-Asset Hourly Consumption
        </h6>
        <Form.Select
          value={selectedId}
          onChange={(e) => handleAssetChange(e.target.value)}
          style={{
            width: 'auto',
            minWidth: '250px',
            fontSize: 'var(--font-size-sm)',
            borderColor: 'var(--color-neutral-300)',
          }}
        >
          {assets.map((asset) => (
            <option key={asset.id} value={asset.id}>
              {asset.name}
            </option>
          ))}
        </Form.Select>
      </div>

      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)', marginBottom: 'var(--space-3)' }}>
        Hourly power consumption
        {shouldShowTemp && ' and indoor temperature profile'}
        {' '}for selected flexible asset.
      </p>

      <Chart options={chartOptions} series={series} type="bar" height={350} />
    </div>
  )
}

export default FlexAssetConsumptionChart
